import { Link } from "react-router-dom";

const LOGO = "/assets/images/kiddo-logo.png";
const WORDMARK = "/assets/images/kiddo-wordmark.png";

/**
 * Site-wide footer shared by the landing page and every public marketing /
 * informational route via PublicShell. Intentionally NOT rendered on the
 * authenticated dashboard (AppShell).
 *
 * Mobile layout: 2-column grid for brand + 3 link columns sit in their own
 * row underneath, all left-aligned so the column headers line up cleanly.
 */
export default function SiteFooter() {
  return (
    <footer className="relative z-10 pt-12 pb-8 border-t border-plum/10 dark:border-slate-800 bg-cream dark:bg-slate-950/80" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-6 md:gap-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-2 text-center md:text-left">
            <Link to="/" className="inline-flex items-center gap-2 mb-3">
              <img src={LOGO} alt="" className="w-8 h-8 rounded-[22%]" loading="lazy" decoding="async" />
              <img src={WORDMARK} alt="Kiddo+" className="h-7 w-auto dark:brightness-0 dark:invert" loading="lazy" decoding="async" />
            </Link>
            <p className="text-inkmid dark:text-slate-400 text-sm leading-relaxed max-w-xs mx-auto md:mx-0">
              Personalized child development guidance, activities, and progress tracking designed by therapists and tailored to your child.
            </p>
          </div>

          {/* 4 link columns — 2x2 on mobile, 4-up on desktop */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-6 md:col-span-4 text-center md:text-left">
            {COLUMNS.map((col) => (
              <div key={col.title}>
                <h4 className="font-heading font-bold mb-3 text-inkdark dark:text-slate-100 text-sm sm:text-base">{col.title}</h4>
                <ul className="space-y-2">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      {l.external ? (
                        <a
                          href={l.to}
                          target={l.to.startsWith("http") ? "_blank" : undefined}
                          rel={l.to.startsWith("http") ? "noopener noreferrer" : undefined}
                          className="text-xs sm:text-sm text-inkmid dark:text-slate-400 hover:text-coral dark:hover:text-coral"
                        >
                          {l.label}
                        </a>
                      ) : l.to.startsWith("/#") ? (
                        <Link to={l.to} className="text-xs sm:text-sm text-inkmid dark:text-slate-400 hover:text-coral dark:hover:text-coral">
                          {l.label}
                        </Link>
                      ) : (
                        <Link to={l.to} className="text-xs sm:text-sm text-inkmid dark:text-slate-400 hover:text-coral dark:hover:text-coral">
                          {l.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-plum/10 dark:border-slate-800 flex flex-col sm:flex-row gap-4 justify-between items-center text-center">
          <p className="text-xs text-inkmid dark:text-slate-500">© 2026 KiddoPlus. All rights reserved.</p>
          <div className="flex flex-wrap gap-x-4 gap-y-2 justify-center text-xs text-inkmid dark:text-slate-500">
            <Link to="/privacy-policy" className="hover:text-coral dark:hover:text-coral">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-coral dark:hover:text-coral">Terms &amp; Conditions</Link>
            <Link to="/faqs" className="hover:text-coral dark:hover:text-coral">FAQs</Link>
            <Link to="/success-stories" className="hover:text-coral dark:hover:text-coral">Success Stories</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

const COLUMNS = [
  { title: "Product", links: [
    { label: "Features", to: "/#features",  external: false },
    { label: "Pricing",  to: "/#plans",     external: false },
    { label: "Courses",  to: "/courses",    external: false },
    { label: "Resources",to: "/resources",  external: false },
  ]},
  { title: "Free Tools", links: [
    { label: "AI Video Assessment",    to: "/ai-video-assessment",   external: false },
    { label: "Development Milestones", to: "/milestones",            external: false },
    { label: "Free Screening",         to: "/free-screening",        external: false },
    { label: "Calculate Child Age",    to: "/child-age-calculator",  external: false },
  ]},
  { title: "Company", links: [
    { label: "About",       to: "/about",                          external: false },
    { label: "FAQs",        to: "/faqs",                           external: false },
    { label: "Help Center", to: "/help-center",                    external: false },
    { label: "Contact",     to: "mailto:support@kiddoplus.com",    external: true  },
  ]},
  { title: "Connect", links: [
    { label: "Instagram", to: "https://www.instagram.com/kiddoplus.ot",   external: true },
    { label: "YouTube",   to: "https://youtube.com/@kiddo-plus",          external: true },
    { label: "WhatsApp",  to: "https://wa.me/918877353514?text=Hi",       external: true },
    { label: "Email",     to: "mailto:support@kiddoplus.com",             external: true },
  ]},
];
